import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { decomposeSelectQuantity, bucketVialTotal, selectLinePrice, vialCountForVariant } from "@/lib/selectDecompose";

const CartContext = createContext(null);
const STORAGE_KEY = "peptidelab_cart_v1";
const HYDRATED_FOR_KEY = "peptidelab_cart_hydrated_for";
const CLEARED_AT_KEY = "peptidelab_cart_cleared_at";
// Window during which we trust a locally-empty cart over the server cart.
// After 30 min we assume a fresh session and re-hydrate from server normally
// (so cross-device sync still works when the user adds items elsewhere).
const RECENT_CLEAR_MS = 30 * 60 * 1000;

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); }
    catch { return []; }
  });
  const [saved, setSaved] = useState([]);
  const [hydrated, setHydrated] = useState(false);
  // Authoritative shipping thresholds + flat rates from /api/shipping-info
  // (Admin/DB source-of-truth). `null` while loading — the UI treats
  // unloaded config as "not yet qualified for FREE".
  const [shippingCfg, setShippingCfg] = useState(null);
  // iter207 · Global Select-tier ladder from /api/select-ladder. Cached at
  // mount so cart-side rebalance (setQty / remove) can decompose Select
  // buckets without an extra fetch. Per-product overrides are stashed on
  // each Select cart line at add-time in `select_effective_ladder`.
  const [selectLadderGlobal, setSelectLadderGlobal] = useState({ 3: 0.05, 5: 0.10 });
  const saveTimer = useRef(null);

  // Hydrate shipping config once per authenticated session. The endpoint
  // is auth-gated (buyer-facing), so we only fetch after login.
  useEffect(() => {
    if (!user) { setShippingCfg(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/shipping-info");
        if (!cancelled) setShippingCfg(data);
      } catch { /* leave null; UI will not falsely display FREE */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // iter207 · Fetch the global Select ladder once so cart-side rebalance
  // has an authoritative fallback when a line was added before the
  // per-line `select_effective_ladder` field existed (legacy carts).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/select-ladder");
        if (cancelled) return;
        setSelectLadderGlobal({
          3: Number(data?.discount_3vial || 0),
          5: Number(data?.discount_5vial || 0),
          10: Number(data?.discount_10vial || 0),
        });
      } catch { /* keep defaults */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Always mirror to localStorage so unauthenticated / offline browsing works.
  // Also stamp a cleared_at timestamp whenever the cart transitions to empty
  // so the hydration fast-path (below) can reliably distinguish "user just
  // cleared" from "cold-start empty" (which could still have server items).
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    if (items.length === 0) {
      localStorage.setItem(CLEARED_AT_KEY, String(Date.now()));
    }
  }, [items]);

  // On sign-in, hydrate cart + saved list from server exactly once per user id.
  // Special case: if this device was already hydrated for this user AND the
  // local cart is now empty, TRUST the local empty state — the user explicitly
  // cleared their cart — and force-push empty to the server instead of pulling
  // stale items back. This is the second line of defense against the "ghost
  // item on refresh" bug in case a keepalive flush was still dropped.
  useEffect(() => {
    if (!user) { setHydrated(false); setSaved([]); return; }
    // Temp-viewer accounts have no cart — skip hydration entirely and mark
    // hydrated so the debounced writer below no-ops immediately.
    if (user.role === "temp_viewer") { setSaved([]); setItems([]); setHydrated(true); return; }
    const alreadyFor = localStorage.getItem(HYDRATED_FOR_KEY);
    if (alreadyFor === user.id && hydrated) return;

    (async () => {
      try {
        // Read current local cart snapshot before any async work.
        const localSnapshot = items;
        const sameUserAsBefore = alreadyFor === user.id;

        // Fast-path: same-device, same-user, user recently emptied the cart.
        // Force the server cart to empty rather than re-hydrating ghosts.
        // Recency guard prevents cross-device sync regression (if the local
        // empty state is stale, we fall through and pull from server normally).
        const clearedAt = parseInt(localStorage.getItem(CLEARED_AT_KEY) || "0", 10);
        const recentClear = clearedAt && (Date.now() - clearedAt) < RECENT_CLEAR_MS;
        if (sameUserAsBefore && localSnapshot.length === 0 && recentClear) {
          try {
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cart`, {
              method: "PUT",
              credentials: "include",
              keepalive: true,
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ items: [] }),
            });
          } catch { /* fine */ }
          try {
            const { data: savedList } = await api.get("/saved");
            setSaved(Array.isArray(savedList) ? savedList : []);
          } catch { /* fine */ }
          localStorage.setItem(HYDRATED_FOR_KEY, user.id);
          setHydrated(true);
          return;
        }

        const [{ data: cart }, { data: savedList }] = await Promise.all([
          api.get("/cart"),
          api.get("/saved"),
        ]);
        const serverItems = Array.isArray(cart?.items) ? cart.items : [];
        // iter185.2 — drop stale zero-priced Supply lines from the server cart.
        // Legacy Supply items persisted from before the fixed-tier pivot may
        // have price=0 because `product.price` on the tiered product doc is
        // no longer used — `supply_tier_id → tier.price` is the source of
        // truth. Zero-priced Supply lines are meaningless and should not
        // hydrate back into the buyer's cart.
        const cleanServerItems = serverItems.filter((s) => {
          const isSupply = (s.tier || "supply").toLowerCase() === "supply";
          const priced = Number(s.price || 0) > 0;
          return !isSupply || priced;
        });
        if (cleanServerItems.length > 0) {
          // Merge server + current local by (product_id, variant) — the server
          // cart is only trusted when this is a fresh device / new session for
          // the user (sameUserAsBefore=false). Otherwise, if we get here with
          // sameUserAsBefore=true, the local cart is non-empty and we merge.
          setItems((local) => {
            if (local.length === 0) {
              toast.success("We saved your cart from last time.");
              return cleanServerItems;
            }
            const map = new Map(local.map((i) => [`${i.product_id}::${i.variant || ""}::${i.variant_mg || ""}`, { ...i }]));
            for (const s of cleanServerItems) {
              const k = `${s.product_id}::${s.variant || ""}::${s.variant_mg || ""}`;
              const existing = map.get(k);
              if (existing) {
                existing.quantity = Math.max(existing.quantity, s.quantity);
                // Prefer the locally-added price when it's truthful — protects
                // against a stale zero-priced server entry overwriting a
                // fresh, correctly-priced local line.
                if (Number(existing.price || 0) <= 0 && Number(s.price || 0) > 0) {
                  existing.price = s.price;
                }
              } else {
                map.set(k, s);
              }
            }
            const merged = Array.from(map.values());
            if (merged.length > local.length) {
              toast.success("Merged your saved cart with your current one.");
            }
            return merged;
          });
        }
        setSaved(Array.isArray(savedList) ? savedList : []);
        localStorage.setItem(HYDRATED_FOR_KEY, user.id);
        setHydrated(true);
      } catch {
        setHydrated(true);
      }
    })();
  }, [user, hydrated]);

  // Debounced push to server whenever cart changes AND user is signed in.
  // When the cart empties, flush IMMEDIATELY (no debounce) — using
  // `keepalive: true` so the request survives a fast page refresh / tab close
  // race that would otherwise cancel the in-flight fetch and leave stale
  // items on the server (the "ghost item" bug).
  useEffect(() => {
    if (!user || !hydrated) return;
    if (user.role === "temp_viewer") return; // preview-only accounts don't sync a cart
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (items.length === 0) {
      // fetch keepalive: guaranteed delivery even across unload. httpOnly
      // auth cookie is auto-sent with credentials: "include".
      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cart`, {
          method: "PUT",
          credentials: "include",
          keepalive: true,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: [] }),
        }).catch(() => {});
      } catch { /* best-effort */ }
      return;
    }
    saveTimer.current = setTimeout(() => {
      api.put("/cart", { items }).catch(() => {});
    }, 700);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [items, user, hydrated]);

  // Flush the LATEST cart on tab close / navigation away via a keepalive
  // fetch so the request actually leaves the browser during unload. We always
  // send — regardless of whether a debounce timer is pending — because the
  // immediate (empty-cart) fetch above can still race an ultra-fast refresh.
  useEffect(() => {
    if (!user || !hydrated) return;
    if (user.role === "temp_viewer") return; // no cart to flush
    const flush = () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cart`, {
          method: "PUT",
          credentials: "include",
          keepalive: true,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items }),
        }).catch(() => {});
      } catch { /* best-effort */ }
    };
    window.addEventListener("pagehide", flush);
    window.addEventListener("beforeunload", flush);
    // visibilitychange → 'hidden' is the most reliable unload signal on iOS
    // Safari, where pagehide/beforeunload are sometimes skipped.
    const onVis = () => { if (document.visibilityState === "hidden") flush(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("pagehide", flush);
      window.removeEventListener("beforeunload", flush);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [items, user, hydrated]);

  // Canonical cart line key. Two lines are the "same" only if product +
  // kit-variant + mg-variant all match. Any of the variant axes can be null.
  const lineKey = (product_id, variant, variant_mg) => `${product_id}::${variant || ""}::${variant_mg || ""}`;
  const sameLine = (a, b) => a.product_id === b.product_id && (a.variant || null) === (b.variant || null) && (a.variant_mg || null) === (b.variant_mg || null);

  // iter207 · Granny-proof helpers. A Select "bucket" is the set of cart
  // lines for one (product_id, variant_mg) — any mix of 1v/3v/5v variants
  // belongs to the same bucket and MUST always be represented as the
  // cheapest authoritative decomposition of the bucket's total vials.
  // Single-buy and max_qty_per_order products opt OUT of decomposition
  // because they carry an explicit business rule that the ladder cannot
  // override.
  const _isSelectBucketLine = (i) => (
    (i.tier || "supply") === "select"
    && !i.is_byok_kit
    && !i.single_buy
    && !i.max_qty_per_order
    && (i.variant === "1vial" || i.variant === "3vial" || i.variant === "5vial")
  );
  const _selectBucketMatch = (i, product_id, mgKey) => (
    _isSelectBucketLine(i)
    && i.product_id === product_id
    && ((i.variant_mg == null ? null : String(i.variant_mg)) === mgKey)
  );
  const _makeSelectLine = (product, variant_mg, part, unitPrice, ladder) => {
    const price = selectLinePrice(part.n, unitPrice, ladder);
    const kitLbl = part.variant === "1vial" ? "1 vial" : `${part.n}-vial Kit`;
    const unit_label = variant_mg ? `${variant_mg}mg · ${kitLbl}` : kitLbl;
    let sku = product.sku;
    if (variant_mg && Array.isArray(product.variants)) {
      const m = product.variants.find((v) => String(v.mg) === String(variant_mg));
      if (m && typeof m.sku === "string" && m.sku.trim()) sku = m.sku;
    }
    return {
      product_id: product.id,
      name: product.name,
      price,
      quantity: part.quantity,
      image_url: product.image_url,
      sku,
      max_qty_per_order: null,
      tier: "select",
      variant: part.variant,
      variant_mg: variant_mg == null ? null : String(variant_mg),
      unit_label,
      kit_vial_count: null,
      single_buy: false,
      single_vial_price: +Number(unitPrice).toFixed(2),
      vial_count: part.n,
      retail_line_price: +(Number(unitPrice) * part.n).toFixed(2),
      kit_savings: +Math.max(0, Number(unitPrice) * part.n - price).toFixed(2),
      select_effective_ladder: { 3: Number(ladder[3] || 0), 5: Number(ladder[5] || 0), 10: Number(ladder[10] || 0) },
    };
  };
  // Replace the (product_id, variant_mg) bucket in `prev` with a fresh
  // best-eligible decomposition of `totalVials`. Non-bucket lines untouched.
  const _rebalanceSelectBucket = (prev, product, variant_mg, totalVials, unitPrice, ladder) => {
    const mgKey = variant_mg == null ? null : String(variant_mg);
    const other = prev.filter((i) => !_selectBucketMatch(i, product.id, mgKey));
    if (totalVials <= 0) return other;
    const parts = decomposeSelectQuantity(totalVials, unitPrice, ladder);
    const fresh = parts.map((p) => _makeSelectLine(product, mgKey, p, unitPrice, ladder));
    return [...other, ...fresh];
  };

  const add = (product, quantity = 1, opts = {}) => {
    const cap = product.max_qty_per_order || null;
    const productTier = (product.tier || "supply").toLowerCase();
    const variant = opts.variant ?? null;              // "3vial"/"5vial"/null
    const variant_mg = opts.variant_mg ?? null;         // "5"/"10"/"15"/null
    // 10VK isolation (owner directive 2026-10-12). 10-Vial Kits must stand
    // alone — they can't share a cart with Supply vials or Select kits.
    // Discriminator mirrors the backend test used in orders.py (kit_vial_count
    // === 10 AND testing_status === "untested"). This runs BEFORE the tier
    // guard below so a 10VK add never falls into the "supply vs select" path.
    const incomingIs10vk = Number(product.kit_vial_count) === 10;
    const currentIs10vk = items.length > 0 && Number(items[0].kit_vial_count) === 10;
    if (items.length > 0 && currentIs10vk !== incomingIs10vk) {
      toast.error(
        "10-Vial Kits ship separately. Please check out or clear your current cart before adding this item.",
        { duration: 6000 },
      );
      return false;
    }
    // iter185.2 — Supply fixed-tier kits (`kit_vial_count`, no variant) were
    // rendering as "1 vial" in cart because they hit the isSingleVial fallback.
    // Persist the kit size and give the line a descriptive default label so
    // downstream (Cart, summary, admin) don't have to guess.
    const supplyKitCount = (productTier === "supply" || productTier === "10vk") && !variant
      ? Number(product.kit_vial_count) || null
      : null;
    const defaultLabel = variant_mg
      ? `${variant_mg}mg`
      : variant === "3vial" ? "3 Vial Kit"
      : variant === "5vial" ? "5 Vial Kit"
      : variant === "10vial" ? "10 Vial Kit"
      : (supplyKitCount && supplyKitCount > 1 ? `${supplyKitCount}-vial Kit` : (product.unit_label || "10 vials/kit"));
    const unitLabel = opts.unit_label ?? defaultLabel;
    const unitPrice = opts.price ?? product.price;
    // Enforce no-mixing of tiers.
    const currentTier = items.length > 0 ? (items[0].tier || "supply").toLowerCase() : null;
    if (currentTier && currentTier !== productTier) {
      const other = currentTier === "select" ? "Research 1617" : "Research Supply";
      const wanted = productTier === "select" ? "Research 1617" : "Research Supply";
      toast.error(
        `Your cart contains ${other} items. Please check out or clear that cart before shopping ${wanted}.`,
        { duration: 6000 }
      );
      return false;
    }

    // iter207 · Granny-proof: Select-tier adds decompose the CURRENT add
    // into the cheapest set of authoritative purchase options. The result
    // is emitted as one or more (variant, qty) lines that share the cart's
    // normal (product_id, variant, variant_mg) merge key — so re-adding
    // the same kit twice increments an existing kit line rather than
    // rebalancing the whole bucket. Cart is a faithful display of what the
    // PDP established; per-line rebalancing on cart-side mutations was
    // removed after RHM UX review (SMART PDP → SIMPLE CART).
    if (
      productTier === "select"
      && !cap
      && !product.single_buy
      && !opts.is_byok_kit
      && (variant === "1vial" || variant === "3vial" || variant === "5vial" || variant === "10vial")
    ) {
      const nAdd = vialCountForVariant(variant);
      const vialsToAdd = Math.max(0, nAdd * (Number(quantity) || 0));
      if (vialsToAdd <= 0) return;
      let unit = Number(opts.unit_price);
      if (!Number.isFinite(unit) || unit <= 0) {
        if (variant_mg && Array.isArray(product.variants)) {
          const m = product.variants.find((v) => String(v.mg) === String(variant_mg));
          if (m && m.price != null) unit = Number(m.price);
        }
        if (!Number.isFinite(unit) || unit <= 0) unit = Number(product.price || 0);
      }
      const eff = opts.effective_ladder || selectLadderGlobal;
      const ladder = { 3: Number(eff?.[3] || 0), 5: Number(eff?.[5] || 0), 10: Number(eff?.[10] || 0) };
      // 10-vial kits are only ever created by an explicit 10-vial pick — never by decomposition.
      const parts = variant === "10vial"
        ? [{ variant: "10vial", n: 10, quantity: Number(quantity) || 0 }]
        : decomposeSelectQuantity(vialsToAdd, unit, ladder);
      if (parts.length === 0) return;
      const mgKey = variant_mg == null ? null : String(variant_mg);
      setItems((prev) => {
        const next = [...prev];
        for (const p of parts) {
          const line = _makeSelectLine(product, mgKey, p, unit, ladder);
          const idx = next.findIndex((i) => sameLine(i, { product_id: product.id, variant: line.variant, variant_mg: mgKey }));
          if (idx > -1) {
            next[idx] = {
              ...next[idx],
              quantity: (Number(next[idx].quantity) || 0) + line.quantity,
              // refresh authoritative fields on merge so mid-session ladder edits
              // or fresh unit prices win over the stale line
              price: line.price,
              single_vial_price: line.single_vial_price,
              vial_count: line.vial_count,
              retail_line_price: line.retail_line_price,
              kit_savings: line.kit_savings,
              select_effective_ladder: line.select_effective_ladder,
              unit_label: line.unit_label,
            };
          } else {
            next.push(line);
          }
        }
        return next;
      });
      return;
    }

    const candidate = { product_id: product.id, variant, variant_mg };
    setItems((prev) => {
      const idx = prev.findIndex((i) => sameLine(i, candidate));
      const capMsg = (name) => cap === 1
        ? `One vial maximum — ${name} is sold one vial at a time.`
        : `${name} is limited to ${cap} per order.`;
      if (idx > -1) {
        const copy = [...prev];
        const desired = copy[idx].quantity + quantity;
        const nextQty = cap ? Math.min(desired, cap) : desired;
        if (cap && desired > cap) {
          toast.info(capMsg(product.name));
        }
        // iter185.2 — refresh price on dedupe: if the incoming line carries
        // a truthful price, use it (protects against a stale $0 line lingering
        // from pre-fix hydration). Otherwise keep whatever's on the line.
        const nextPrice = Number(unitPrice) > 0 ? unitPrice : (copy[idx].price || 0);
        copy[idx] = { ...copy[idx], quantity: nextQty, max_qty_per_order: cap, tier: productTier, variant, variant_mg, unit_label: unitLabel, price: nextPrice, single_buy: !!product.single_buy, kit_vial_count: supplyKitCount || copy[idx].kit_vial_count || null };
        return copy;
      }
      const initialQty = cap ? Math.min(quantity, cap) : quantity;
      if (cap && quantity > cap) {
        toast.info(capMsg(product.name));
      }
      // iter_135 — retail-preview scaffolding. Same math the packing slip uses,
      // computed at add-time so the Cart page can render "You'll save $X"
      // without an extra fetch. Only meaningful for Select-tier lines (Supply
      // is flat, BYOK carries its own byok_subtotal/byok_discount_amount).
      let single_vial_price = null;
      let vial_count = null;
      let retail_line_price = null;
      let kit_savings = null;
      if ((productTier === "select") && (variant === "1vial" || variant === "3vial" || variant === "5vial" || variant === "10vial")) {
        // Single-vial base: mg-variant price if the product carries variants,
        // else the product-level price.
        const nBase = variant === "3vial" ? 3 : variant === "5vial" ? 5 : variant === "10vial" ? 10 : 1;
        let unit = Number(product.price || 0);
        if (variant_mg && Array.isArray(product.variants)) {
          const m = product.variants.find((v) => String(v.mg) === String(variant_mg));
          if (m && m.price != null) unit = Number(m.price);
        }
        single_vial_price = +unit.toFixed(2);
        vial_count = nBase;
        retail_line_price = +(single_vial_price * nBase).toFixed(2);
        kit_savings = +Math.max(0, retail_line_price - Number(unitPrice)).toFixed(2);
      }
    // iter205 · Effective per-variant SKU. When the buyer picked an mg
    // variant, prefer that variant's SKU (product.variants[mg].sku, which
    // the API already emits with the parent-fallback baked in via
    // _sorted_variants). Falls back to product.sku for flat products.
    let effective_sku = product.sku;
    if (variant_mg && Array.isArray(product.variants)) {
      const m = product.variants.find((v) => String(v.mg) === String(variant_mg));
      if (m && typeof m.sku === "string" && m.sku.trim()) effective_sku = m.sku;
    }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        price: unitPrice,
        quantity: initialQty,
        image_url: product.image_url,
        sku: effective_sku,
        max_qty_per_order: cap,
        tier: productTier,
        variant,
        variant_mg,
        unit_label: unitLabel,
        // iter185.2 — carry the Supply kit size on the line so Cart display
        // and downstream serializers can render "3-vial Kit" instead of "1 vial".
        kit_vial_count: supplyKitCount,
        // Persist the min-kit exemption on the cart line so re-hydrating from
        // localStorage keeps the correct rule after a refresh.
        single_buy: !!product.single_buy,
        // iter_135 — retail preview
        single_vial_price,
        vial_count,
        retail_line_price,
        kit_savings,
      }];
    });
  };

  // iter207 · Remove a cart line. Cart-side Select bucket rebalancing was
  // removed after RHM UX review — the Cart is a faithful display of the
  // configuration the PDP established. Removing a line just drops it.
  const remove = (product_id, variant = null, variant_mg = null) => setItems((p) =>
    p.filter((i) => !sameLine(i, { product_id, variant, variant_mg }))
  );

  // Add a Build-Your-Own-Kit line. Server has already priced it via /byok/quote.
  // We stash the server-authoritative pieces (kit_price, size, item list) plus
  // a unique product_id (so two identical BYOK kits stay as separate lines).
  const addByokKit = (kit) => {
    // Enforce tier no-mix (BYOK is always Select).
    const currentTier = items.length > 0 ? (items[0].tier || "supply").toLowerCase() : null;
    if (currentTier && currentTier !== "select") {
      toast.error(
        `Your cart contains Research Supply items. Please check out or clear that cart before adding a Research 1617 kit.`,
        { duration: 6000 }
      );
      return;
    }
    const uid = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setItems((prev) => [...prev, {
      product_id: `byok-${uid}`,
      name: `Build-Your-Own ${kit.size}-Vial Kit`,
      price: kit.kit_price,
      quantity: 1,
      image_url: null,
      sku: `BYOK-${kit.size}`,
      max_qty_per_order: null,
      tier: "select",
      variant: null,
      variant_mg: null,
      unit_label: `Build-Your-Own ${kit.size}-Vial Kit`,
      is_byok_kit: true,
      byok_size: kit.size,
      byok_items: kit.items,
      byok_subtotal: kit.subtotal,
      byok_discount_rate: kit.discount_rate,
      byok_discount_amount: kit.discount_amount,
    }]);
  };
  // iter207 · Set a cart line's quantity. Cart-side rebalancing for Select
  // buckets was removed after RHM UX review (SMART PDP → SIMPLE CART):
  // the SELECT cart no longer surfaces a quantity stepper for kit/vial
  // lines, so setQty won't fire from the UI for those lines. Kept as a
  // straight-through mutator for any programmatic caller (e.g. admin
  // manual invoice, Supply-tier stepper).
  const setQty = (product_id, quantity, variant = null, variant_mg = null) => setItems((p) =>
    p.map((i) => {
      if (!sameLine(i, { product_id, variant, variant_mg })) return i;
      const cap = i.max_qty_per_order || null;
      let next = Math.max(1, quantity);
      if (cap && next > cap) {
        toast.info(cap === 1
          ? `One vial maximum — ${i.name} is sold one vial at a time.`
          : `${i.name} is limited to ${cap} per order.`);
        next = cap;
      }
      return { ...i, quantity: next };
    })
  );
  const clear = () => setItems([]);

  // Save For Later — backend-persisted, cross-device.
  const saveForLater = async (product_id, variant = null, variant_mg = null) => {
    const sameLine = (i) => i.product_id === product_id && (i.variant || null) === (variant || null) && (i.variant_mg || null) === (variant_mg || null);
    const line = items.find(sameLine);
    if (!line) return;
    if (!user) {
      toast.error("Sign in to save items across devices.");
      return;
    }
    try {
      const { data } = await api.post("/saved", {
        product_id: line.product_id,
        name: line.name,
        price: line.price,
        image_url: line.image_url || "",
        sku: line.sku || "",
        variant: line.variant || null,
        variant_mg: line.variant_mg || null,
        unit_label: line.unit_label || null,
      });
      // Optimistically remove only this line + prepend to saved list (dedupe by product_id)
      setItems((prev) => prev.filter((i) => !sameLine(i)));
      setSaved((prev) => [data, ...prev.filter((s) => !(s.product_id === product_id && (s.variant || null) === (variant || null)))]);
      toast.success(`Moved "${line.name}" to Saved for later`);
    } catch {
      toast.error("Couldn't save — try again in a moment");
    }
  };

  const savedMatch = (product_id, variant) => (x) => x.product_id === product_id && (x.variant || null) === (variant || null);
  const savedDeleteUrl = (product_id, variant) => `/saved/${product_id}${variant ? `?variant=${encodeURIComponent(variant)}` : ""}`;
  const moveSavedToCart = async (product_id, variant = null) => {
    const s = saved.find(savedMatch(product_id, variant));
    if (!s) return;
    // Add to cart first, then delete from saved on the server
    const opts = s.variant || s.variant_mg ? { variant: s.variant || undefined, variant_mg: s.variant_mg || undefined, price: s.price, unit_label: s.unit_label || undefined } : {};
    add({ id: s.product_id, name: s.name, price: s.price, image_url: s.image_url, sku: s.sku }, 1, opts);
    setSaved((prev) => prev.filter((x) => !savedMatch(product_id, variant)(x)));
    if (user) {
      try { await api.delete(savedDeleteUrl(product_id, variant)); } catch { /* re-hydrate next login */ }
    }
    toast.success(`Moved "${s.name}" back to your cart`);
  };

  const removeSaved = async (product_id, variant = null) => {
    const s = saved.find(savedMatch(product_id, variant));
    setSaved((prev) => prev.filter((x) => !savedMatch(product_id, variant)(x)));
    if (user && s) {
      try { await api.delete(savedDeleteUrl(product_id, variant)); } catch { /* silent */ }
    }
  };

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  // iter_135 — retail preview totals. Kit savings sums up per-line savings
  // (Select ladder discount) + BYOK per-kit savings. Discount codes are
  // applied at checkout, not on cart, so we do NOT include them here.
  const retailSubtotal = items.reduce((s, i) => {
    if (i.is_byok_kit) {
      // BYOK kits: retail = the un-discounted vial-total (byok_subtotal); kit-price already reflects the discount.
      const retail = Number(i.byok_subtotal ?? i.price);
      return s + retail * (i.quantity || 1);
    }
    const retail = Number(i.retail_line_price ?? i.price);
    return s + retail * i.quantity;
  }, 0);
  const kitSavings = items.reduce((s, i) => {
    if (i.is_byok_kit) return s + Number(i.byok_discount_amount || 0) * (i.quantity || 1);
    return s + Number(i.kit_savings ?? 0) * i.quantity;
  }, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);
  // Kits that count toward the tier minimum. Products flagged as `single_buy`
  // (e.g., bacteriostatic water) are exempt — they don't count for or against
  // the minimum, so a cart of just single-buy items always passes.
  const nonSingleBuyCount = items.reduce((s, i) => s + (i.single_buy ? 0 : i.quantity), 0);
  // Tier of the current cart (all items must share the same tier — enforced by add()).
  const cartTier = items.length > 0 ? (items[0].tier || "supply").toLowerCase() : null;
  const isSelectCart = cartTier === "select";
  // 10VK carts stand alone — detected via the first line's kit_vial_count
  // (10VK adds stamp `kit_vial_count: 10`). Matches the backend discriminator
  // combined with the server-side testing_status check. 10VK carts never
  // qualify for free shipping (owner directive 2026-10-12).
  const cartIs10vk = items.length > 0 && Number(items[0].kit_vial_count) === 10;
  // iter185.4 — Kit minimum REMOVED across both tiers. Buyers can check out
  // with a single kit / vial. Keep the constants at 1 (rather than deleting)
  // so downstream `nonSingleBuyCount >= MIN_KITS` math stays trivially true
  // and admin can re-enable per-tier via config later without a code deploy.
  const MIN_KITS_SUPPLY = 1;
  const MIN_KITS_SELECT = 1;
  const MIN_KITS = isSelectCart ? MIN_KITS_SELECT : MIN_KITS_SUPPLY;
  // Shipping thresholds + flat rates are read from the authoritative
  // /api/shipping-info endpoint (Admin/DB). Never hard-coded here.
  // `null` while the config is loading so the UI cannot falsely flip
  // to FREE before the real numbers arrive.
  const FREE_SHIP_THRESHOLD = isSelectCart
    ? shippingCfg?.free_shipping_threshold_select ?? null
    : shippingCfg?.free_shipping_threshold ?? null;
  const FLAT_SHIP_RATE = isSelectCart
    ? shippingCfg?.flat_rate_select ?? null
    : shippingCfg?.flat_rate ?? null;
  // If EVERY item in the cart is single-buy, `nonSingleBuyCount === 0` and we
  // skip the min-kit rule entirely. Otherwise, the non-single-buy portion
  // must clear the minimum.
  const kitsRemaining = nonSingleBuyCount === 0 ? 0 : Math.max(0, MIN_KITS - nonSingleBuyCount);
  const meetsMinimum = items.length > 0 && (nonSingleBuyCount === 0 || nonSingleBuyCount >= MIN_KITS);
  const hasSingleBuyMix = nonSingleBuyCount > 0 && items.some((i) => i.single_buy);
  // Free-ship qualification only after config has loaded — never flip
  // true against an unknown threshold. 10VK carts are hard-blocked from
  // free shipping regardless of subtotal.
  const freeShipping = !cartIs10vk && FREE_SHIP_THRESHOLD !== null && subtotal >= FREE_SHIP_THRESHOLD;
  // Cart no longer surfaces a dollar shipping figure; the real rate is
  // computed live via Shippo on the /checkout page after an address is
  // entered. Keep `shipping` at 0 here so downstream math (total) stays
  // safe, and let the UI show "Calculated at checkout" instead of a number.
  const shipping = 0;
  const freeShipRemaining = (cartIs10vk || FREE_SHIP_THRESHOLD === null)
    ? null
    : Math.max(0, FREE_SHIP_THRESHOLD - subtotal);
  const total = subtotal + shipping;

  return (
    <CartContext.Provider value={{
      items, add, addByokKit, remove, setQty, clear, lineKey,
      saved, saveForLater, moveSavedToCart, removeSaved,
      subtotal, count, MIN_KITS, kitsRemaining, meetsMinimum, hasSingleBuyMix,
      cartTier, isSelectCart, cartIs10vk,
      FREE_SHIP_THRESHOLD, FLAT_SHIP_RATE, freeShipping, freeShipRemaining, shipping, total,
      retailSubtotal, kitSavings,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
